import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import { getCategories } from "@/services/categoryServices/categoryAction";
import { getPosts } from "@/services/blogServices/blogActions";
import { getPagesByCurrentSite } from "@/services/pageServices/pageAction";
import { getProductProperties } from "@/services/productPropertiesServices/ProductPropertiesActions";
import {
  toProductOptions,
  toCategoryOptions,
  toSubCategoryOptions,
  toPostOptions,
} from "../dropdownOptions";
import type { DropdownOption } from "../dropdownOptions";

/** The part of the page being edited this hook reads (the rest passes through). */
type PageToUpdate = {
  page_configuration?: { type?: string; object_target?: { category?: unknown } | null };
  [field: string]: unknown;
};

/** A ProductProperties row as the productproperties slice holds it. */
type ProductPropertiesRow = {
  subcategory?: { hostsProducts?: boolean; category?: unknown } | null;
};

/**
 * useDropdownOptions — the dropdown options of HandleContent's
 * `status="required"` host plus its fetch-if-empty effect (SCRUM-909 lifted
 * them out of the host). Since SCRUM-2167 every option list is derived during
 * render from its slice; none is copied into state by an effect, so a
 * page_type change after load re-narrows the products on the next render.
 * Shapes come from the shared builders (../dropdownOptions, SCRUM-907).
 *
 * Pinned invariants (preserved from the pre-hook code):
 *  - Required product SOURCE deviation: products come from
 *    `categories.subCategory_by_id` (a { products: [...] } doc), NOT the
 *    productproperties slice HandleContent (optional) uses — EXCEPT on a
 *    sub_category page (SCRUM-1719, SCRUM-1413 D4): there the picker draws
 *    from the category's trunks (`trunkPoolForCategory` below), read off the
 *    productproperties slice, because a view node's own `products[]` is empty
 *    (SCRUM-1669) and the listing's membership is the page's productslist1
 *    block, not the node. getSubCategorybyId is therefore no longer dispatched
 *    here; the subCategory_by_id read stays for the category-narrowing branch.
 *  - Category-narrowing branch: on a "category" page whose page_type carries a
 *    real subcategories array, the product list is narrowed to that page's
 *    products. Since SCRUM-1651 getPageById answers a category target WITH
 *    `subcategories[].products`, so the update flow no longer produces the
 *    slim {_id}-only page_type object this branch used to fall back on; the
 *    `Array.isArray` test stays as the belt to the write boundary's braces
 *    (pagesSlice.ts pageTypePayloadDefect) — mirrors selectProductOptions.
 *  - SCRUM-571 guard: the fetch effect depends on `.length` /
 *    `Object.keys(...).length`, NEVER the slice reference — an empty response
 *    creates a new reference and would otherwise re-dispatch the "fetch if
 *    empty" calls forever.
 */
/**
 * The id a populated-or-bare ref points at, as a string; "" when absent.
 * `object_target.category` is `{ _id, slug }` since SCRUM-1651 but a page
 * saved before it, or the create flow, may still carry the bare id.
 */
const refId = (ref: unknown): string =>
  String((ref as { _id?: unknown } | null | undefined)?._id ?? ref ?? "");

/**
 * SCRUM-1719 — the listing picker's pool on a sub_category page: every
 * ProductProperties row whose subcategory is a trunk (`hostsProducts === true`)
 * of the page's category. Pure, so the same function measures the pool on a
 * seeded stack (ticket criterion 3) and in the jest cases.
 * `productproperties` is the productproperties slice (GET /api/productproperties,
 * country-filtered); `categoryRef` is the page's `object_target.category`,
 * populated or bare.
 */
export const trunkPoolForCategory = <T extends ProductPropertiesRow>(
  productproperties: T[] | undefined,
  categoryRef: unknown
): T[] => {
  const categoryId = refId(categoryRef);
  if (!categoryId) return [];
  return (productproperties ?? []).filter(
    (row) =>
      row?.subcategory?.hostsProducts === true &&
      refId(row.subcategory.category) === categoryId
  );
};

export function useDropdownOptions({
  page_to_update,
  pageType,
}: {
  page_to_update: PageToUpdate;
  /** The update screen's page-type pick, passed down since SCRUM-2216; absent on screens without that step. */
  pageType?: PageTypeLike;
}) {
  const dispatch = useAppDispatch();
  const isSubCategoryPage =
    page_to_update.page_configuration?.type === "sub_category";
  // Required sources products from the sub_category doc, not productproperties —
  // except on a sub_category page, where the pool is the category's trunks.
  const products_redux = useAppSelector(
    (state) => state.categories.subCategory_by_id
  );
  const productproperties_redux = useAppSelector(
    (state) => state.productproperties.productproperties
  );
  const categories_redux = useAppSelector((state) => state.categories.categories);
  const posts_redux = useAppSelector((state) => state.blog.posts);
  const pages_redux = useAppSelector((state) => state.pages.pages);

  // Fetch-if-empty. SCRUM-571: depend on sizes, not slice references, or an
  // empty response (new reference every time) re-triggers the dispatch forever.
  useEffect(() => {
    // SCRUM-1719: a sub_category page's pool is the productproperties slice
    // (the same fetch HandleContent (optional) makes), not the node's products[].
    if (isSubCategoryPage && productproperties_redux.length === 0) {
      dispatch(getProductProperties());
    }
    if (categories_redux.length === 0) dispatch(getCategories());
    if (!posts_redux || posts_redux.length === 0) dispatch(getPosts());
    if (!pages_redux || pages_redux.length === 0) dispatch(getPagesByCurrentSite());
  }, [
    dispatch,
    page_to_update,
    isSubCategoryPage,
    productproperties_redux.length,
    categories_redux.length,
    posts_redux?.length,
    pages_redux?.length,
  ]);

  // The option lists are derived during render (SCRUM-2167): they follow the
  // slices and the page_type on every render instead of being copied into
  // state by one effect per slice. The React Compiler memoises them.
  const localProducts: DropdownOption<unknown>[] = isSubCategoryPage
    ? // sub_category page (SCRUM-1719): the category's trunk pool.
      toProductOptions(
        trunkPoolForCategory(
          productproperties_redux,
          page_to_update.page_configuration?.object_target?.category
        )
      )
    : // every other page type: the full list, narrowed to the active category
      // page's products when the page_type carries a real subcategories array.
      narrowToCategoryPage(products_redux?.products, pageType);

  return {
    localProducts,
    localCategories: toCategoryOptions(categories_redux),
    localSubCategories: toSubCategoryOptions(categories_redux),
    localPosts: toPostOptions(posts_redux),
  };
}

/** The page-type pick (UpdatePageContainer state since SCRUM-2216), as far as the narrowing reads it. */
export type PageTypeLike = { type?: string; object?: unknown };

/**
 * Products → options, narrowed to the active category page's products when
 * the page_type is a category carrying a real `subcategories` array (the
 * category-narrowing branch both update hosts share). Shared with the
 * Optional host's hook, which reads its products from another slice.
 */
export const narrowToCategoryPage = <T extends { _id?: unknown; product?: { title?: string } }>(
  products: T[] | null | undefined,
  pageType: PageTypeLike | null | undefined
): DropdownOption<T>[] => {
  const object = pageType?.object;
  const subcategories: unknown =
    typeof object === "object" && object !== null && "subcategories" in object
      ? object.subcategories
      : undefined;
  if (pageType?.type !== "category" || !Array.isArray(subcategories)) {
    return toProductOptions(products);
  }
  const productIdSet = new Set<unknown>(
    subcategories.flatMap((sub: { products?: unknown[] }) => sub.products || [])
  );
  return toProductOptions((products ?? []).filter((option) => productIdSet.has(option._id)));
};
